import { useApolloClient, useMutation } from '@apollo/client';
import { AppRoutePath } from 'config/constants';
import { useAsyncGlobalAction } from 'hooks/useGlobalAction';
import Cookie from 'js-cookie';
import analytics from 'modules/analytics';
import { workspace } from 'modules/analytics/constants';
import { removeTypeName } from 'modules/analytics/util';
import { priorClient } from 'modules/Apollo/priorClient';
import {
  AUTH_WITH_EXT_TOKEN,
  AUTHENTICATE,
  AUTHENTICATE_MFA,
  UNAUTHENTICATE,
} from 'modules/Apollo/queries';
import { logger } from 'modules/logger';
import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { atom, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { isRefreshTokenProvided, logout } from 'utils/token';
import { createContext } from '../common/ui/util/createContext';
import {
  AuthWithExtTokenMutation,
  AuthWithExtTokenMutationVariables,
  AuthenticateMutation,
  AuthenticateMutationVariables,
  UnauthenticateMutation,
  UnauthenticateMutationVariables,
  User,
} from '../generated/graphql';
import { useLiveWorkerGeneration } from 'domains/viewer/Viewer/EscapeHatch/state';
import { createPixelDataSharedWorker } from '../modules/viewer/workers/PixelDataSharedWorker';
import { clearStoredData } from './useClearStoredDataOnClose';
import { useSetUserPermissions } from './useGetUserPermissions';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import useInterval from 'react-use/esm/useInterval';
import { CancelTransferScope } from 'modules/viewer/workers/pixelDataSharedWorkerTypes';
import { WithApolloContext } from 'modules/Apollo/types';

export type Auth = {
  isAuthenticated: boolean;
  error: Nullable<string>; // unknown, LoginComponent is untyped but it only checks if error is not null,
  login: (arg: { email: string; password: string }) => Promise<void>;
  loginMFA: (arg: {
    email: Nullable<string>;
    totp: Nullable<string>;
    mfaSessionId: Nullable<string>;
  }) => Promise<void>;
  authWithExternalToken: (token: string) => Promise<{ caseSmid: string }>;
  logout: () => Promise<void>;
};

export const [AuthProvider, useAuthContext, AuthContext] = createContext<
  Omit<Auth, 'authWithExternalToken'>
>({
  strict: true,
  errorMessage:
    'useAuth: `context` is undefined. Seems you forgot to wrap component within the AuthProvider',
  name: 'AuthContext',
});

export const authState = atom({
  key: 'auth',
  default: isRefreshTokenProvided(),
});

const isAuthedAsExternalUser = atom({
  key: 'ext.auth',
  default: false,
});

export const useIsAuthenticated = (): boolean => useRecoilValue(authState);
export const useIsAuthenticatedAsExternalUser = (): boolean =>
  useRecoilValue(isAuthedAsExternalUser);

/**
 * When a user logs out of one page, the access and refresh token cookies are removed for the origin on all pages.
 * Additionally, a `refresh_token_provided` cookie is set to false, which is what the auth code checks for.
 * Our typical logout flow propagates an event to other pages when a user logs out, which triggers the recoil state updates.
 * However, this event might get lost if the page is hibernating, so this hook will fire and force the unauthenticated state
 * after the page wakes up. Even if the page was hibernating, the cookie changes on one page will still apply to all others
 * in the app.sironamedical.com origin.
 */
export function useMonitorAuthToken() {
  const [isAuthenticated, setAuthenticated] = useRecoilState(authState);

  const checkToken = useCallback(() => {
    const tokenProvided = isRefreshTokenProvided();

    if (tokenProvided !== isAuthenticated) {
      setAuthenticated(tokenProvided);
    }
  }, [isAuthenticated, setAuthenticated]);

  useInterval(checkToken, 1000);
}

/**
 * Identifies a user for analytics after login
 */
function identifyUser(authenticateResponse?: User | null) {
  if (authenticateResponse == null) {
    logger.warn('Empty authentication response when attempting to identify user');
    return;
  }
  const { id, firstName, lastName, email, clinic, reporterSettings } = authenticateResponse;
  const additionalData = {
    segmentData: {
      reporterSettings: removeTypeName(reporterSettings),
    },
  } as const;
  analytics.identify(
    {
      id,
      firstName,
      lastName,
      email,
      clinic: {
        name: clinic?.name || '',
        smid: clinic?.smid || '',
      },
    },
    additionalData
  );

  analytics.track(workspace.usr.login, {
    email,
    clinic: {
      name: clinic?.name ?? '',
      smid: clinic?.smid ?? '',
    },
  });
}

export enum AuthError {
  TOKEN_EXPIRED = 'Token expired',
  TOKEN_INVALID = 'Invalid authentication response',
}

/**
 * Hook for handling all authentication-related operations, which includes: login (with or without
 * MFA), token access, and log out.
 */
const useAuth = ({ context }: WithApolloContext): Auth => {
  const [isAuthenticated, setIsAuthenticated] = useRecoilState(authState);
  const [error, setError] = useState(null);
  const setIsExternalUser = useSetRecoilState(isAuthedAsExternalUser);
  const client = useApolloClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [authenticate] = useMutation<AuthenticateMutation, AuthenticateMutationVariables>(
    AUTHENTICATE,
    {
      context,
    }
  );
  const [authenticateMFA] = useMutation(AUTHENTICATE_MFA, { context });
  const [unauthenticate] = useMutation<UnauthenticateMutation, UnauthenticateMutationVariables>(
    UNAUTHENTICATE,
    { context }
  );
  const [authWithExtToken] = useMutation<
    AuthWithExtTokenMutation,
    AuthWithExtTokenMutationVariables
  >(AUTH_WITH_EXT_TOKEN, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data?.authWithExtToken?.user?.type === 'ExternalIntegrationUser') {
        setIsExternalUser(true);
      }
    },
    onError: (error) => {
      logger.error('Error authenticating with token', { error });
      throw new Error(AuthError.TOKEN_EXPIRED);
    },
    context: {
      ...context,
      telemetry: { ...context.telemetry, fetchPolicy: 'no-cache' },
    },
  });
  // treat loading as false
  // do not use the Suspense version of Split, as that will suspend until a user is available
  // this will not always be the case during logout scenarios
  const [useDedupedPixelWorker] = useFeatureFlagEnabled(FF.PIXEL_WORKER_DEDUPLICATION);
  const generation = useLiveWorkerGeneration();

  const handleLogout = useCallback(async () => {
    try {
      await unauthenticate({
        // Suppress error when unauthenticating without a valid user, such as when a user's refresh token expires.
        onError: () => {},
      });
    } catch (error) {
      logger.error('Unauthenticate failed:', error);
    }

    try {
      client.stop();
      // Priors are fetched through a separate Apollo client/cache (`priorClient`).
      // Stop + clear it on logout too, or a re-login in the same SPA session can
      // serve cached prior-study data (PHI) without a fresh network/auth check.
      priorClient.stop();

      Cookie.set('refresh_token_provided', 'false');
      analytics.reset();

      // VX-4363: connect at the live generation so cancel-transfer/clear-cache reach the worker
      // that actually holds this session's PHI-bearing pixel cache, not the bare gen-0 default.
      const worker = createPixelDataSharedWorker(
        useDedupedPixelWorker,
        undefined,
        false,
        generation
      );
      worker.port.postMessage({
        type: 'cancel-transfer',
        scope: CancelTransferScope.All,
      });
      await clearStoredData(worker);

      setError(null);
      logout(); // Clears local and session storage of not-persisted entries
    } catch (error) {
      logger.error('Logout and cleanup failed:', error);
    } finally {
      const [mainResult, priorResult] = await Promise.allSettled([
        Promise.resolve().then(() => client.clearStore()),
        Promise.resolve().then(() => priorClient.clearStore()),
      ]);
      if (mainResult.status === 'rejected') {
        logger.error('Failed to clear main Apollo cache on logout:', mainResult.reason);
      }
      if (priorResult.status === 'rejected') {
        logger.error('Failed to clear prior-study Apollo cache on logout:', priorResult.reason);
      }
      setIsAuthenticated(false);
    }
  }, [client, setIsAuthenticated, unauthenticate, useDedupedPixelWorker, generation]);

  const handleGlobalLogout = useAsyncGlobalAction('use-auth-logout', handleLogout, {
    callSelf: true,
  });

  const handleAuthenticate = useCallback(() => {
    setIsAuthenticated(true);
  }, [setIsAuthenticated]);

  const setUserPermissions = useSetUserPermissions();

  /** Callback for common code to execute upon a successful authorization */
  const _postAuthHandler = useCallback(
    async (user: User) => {
      logger.debug('Clearing localforage cache on login');
      const worker = createPixelDataSharedWorker();
      // Clear localforage cache on login so that we don't keep stale data and we allow users to
      // login with a different account and start from scratch
      await clearStoredData(worker);

      // Set the refresh token's expiration
      const expires = new Date();
      expires.setDate(expires.getDate() + 30); // Hardcoded because the token doesn't provide one
      Cookie.set('refresh_token_provided', 'true', { expires });

      // Clear auth error state
      setError(null);
      handleAuthenticate();
      // Identify the user for analytics
      identifyUser(user);
    },
    [handleAuthenticate]
  );

  const handleLogin = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      setError(null);
      try {
        const response = await authenticate({ variables: { email, password } });

        const authenticateResponse = response.data?.authenticate;

        if (!authenticateResponse) {
          throw new Error(AuthError.TOKEN_INVALID);
        }

        if (authenticateResponse.__typename === 'MFARequired') {
          const mfaSessionId = authenticateResponse?.mfaSessionId;
          navigate(AppRoutePath.VERIFY_MFA, {
            state: {
              mfa_session: mfaSessionId,
              email,
            },
          });
        } else {
          if (authenticateResponse.__typename !== 'User') return;
          // Extract permissions from user object to avoid storing them in GraphQL cache
          const { permissions, ...user } = authenticateResponse;
          setUserPermissions(permissions);
          // TODO: The typecast to user is not ideal here; we should find a way to avoid it. But
          // it's a lesser of two evils to explicitly typecast than to have `user` defined as any
          // due to an untyped useMutation.
          await _postAuthHandler(user as User);
          const from = location.state?.from;
          navigate(from ?? { pathname: AppRoutePath.WORKLIST });
        }
      } catch (err: any) {
        analytics.error(err, { email });
        setError(err);
        return;
      }
    },
    [authenticate, setUserPermissions, navigate, location.state, _postAuthHandler]
  );

  const handleMFALogin = useCallback(
    async ({
      email,
      totp,
      mfaSessionId,
    }: {
      email: Nullable<string>;
      totp: Nullable<string>;
      mfaSessionId: Nullable<string>;
    }) => {
      setError(null);
      try {
        const response = await authenticateMFA({
          variables: { email, totp, mfaSessionId },
        });

        if (response.data?.authenticateMFA.permissions) {
          setUserPermissions(response.data.authenticateMFA.permissions);
        }

        await _postAuthHandler(response.data?.authenticateMFA);
        const from = location.state?.from;
        navigate(from ?? { pathname: AppRoutePath.WORKLIST });
      } catch (err: any) {
        analytics.error(err, { email });
        setError(err);
        return;
      }
    },
    [authenticateMFA, _postAuthHandler, location.state, navigate, setUserPermissions]
  );

  const handleAuthWithExtToken = useCallback(
    async (token: string) => {
      let data;
      try {
        const resp = await authWithExtToken({ variables: { token } });
        data = resp.data?.authWithExtToken;
      } catch (ex) {
        throw new Error('Invalid authentication response');
      } finally {
        if (!data) {
          // eslint-disable-next-line no-unsafe-finally
          throw new Error('Invalid authentication response');
        }
      }
      const { caseSmid, user } = data;
      const permissions = user?.permissions ?? {};
      setUserPermissions(permissions);
      await _postAuthHandler(user as User);

      return { caseSmid: caseSmid as string };
    },
    [_postAuthHandler, authWithExtToken, setUserPermissions]
  );

  return {
    isAuthenticated,
    logout: handleGlobalLogout,
    login: handleLogin,
    loginMFA: handleMFALogin,
    authWithExternalToken: handleAuthWithExtToken,
    error,
  };
};

export default useAuth;
